import { useState } from "react";
import { Button } from "react-aria-components";

import {
  freshTaskStatusLabel,
  isFreshTaskActive,
  type ConversationFreshTask,
} from "@/features/chat/conversationFreshTasks";

interface Props {
  onConfirm: (taskId: string) => Promise<void>;
  canConfirm: boolean;
  onReadAgain: () => void;
  stale: boolean;
  tasks: ReadonlyArray<ConversationFreshTask>;
}

export function ConversationFreshTaskStatus({ onConfirm, canConfirm, onReadAgain, stale, tasks }: Props) {
  const [submitted, setSubmitted] = useState<string[]>([]);
  const [confirmError, setConfirmError] = useState("");
  const visible = [...tasks]
    .sort((left, right) => {
      const priority = Number(isFreshTaskActive(right)) - Number(isFreshTaskActive(left));
      return priority || Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
    })
    .slice(0, 8);
  if (visible.length === 0 && !stale) return null;

  return (
    <section
      aria-label="当前对话的 CWM 现采任务"
      className="mx-auto mb-5 w-full max-w-[1440px] rounded-[24px] border border-white/80 bg-white/65 px-6 py-5 shadow-[0_10px_28px_rgba(27,47,59,0.07)]"
      data-testid="conversation-fresh-tasks"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">当前数据核实</h2>
          <p className="mt-1 text-sm text-ink-muted">先核对采集范围，确认后才会开始。</p>
        </div>
        {stale || visible.some((task) => task.cancelPending || task.cancelRequestUnknown || task.retryableReadError) ? (
          <Button
            className="rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700"
            onPress={onReadAgain}
          >
            重新读取状态
          </Button>
        ) : null}
      </header>
      {stale ? (
        <p className="mt-3 text-sm text-red-700" role="alert">
          现采状态暂未同步，以下仅为最后一次已读取的结果。
        </p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="mt-4 grid gap-3">
          {visible.map((task) => (
            <li
              className="rounded-2xl border border-slate-200/75 bg-white/70 px-4 py-3"
              data-fresh-task-status={task.status}
              key={task.id}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm text-ink" aria-live="polite">
                  {freshTaskStatusLabel(task)}
                </strong>
                <time className="text-xs text-ink-muted" dateTime={task.updatedAt}>
                  最近同步 {formatTime(task.updatedAt)}
                </time>
              </div>
              <p className="mt-1 break-all text-xs text-ink-muted">任务 ID：{task.id}</p>
              {task.status === "approval_required" && task.plan ? (
                <div className="mt-3 rounded-xl bg-blue-50/70 p-3 text-sm text-ink">
                  <p className="font-medium">将核实以下公开信息</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {task.plan.targets.map((target, index) => (
                      <li key={`${target.subject}-${index}`} className="break-all">
                        {[target.platform, target.country_context, target.subject].filter(Boolean).join(" · ")}
                      </li>
                    ))}
                  </ul>
                  {task.plan.source_choice ? (
                    <>
                      <p className="mt-2">本次重点核实：{task.plan.source_choice.decision_fields.map(freshDecisionFieldLabel).join("、")}</p>
                      <p className="mt-1 text-ink-muted">来源：{task.plan.source_choice.capability_id.startsWith("tiktok.echotik.") ? "EchoTik" : task.plan.source_choice.platform === "TIKTOK_ONE" ? "TikTok One" : task.plan.source_choice.platform} · {freshSourceStabilityLabel(task.plan.source_choice.stability)}。字段能否返回，以本次结果为准。</p>
                    </>
                  ) : <p className="mt-2">核实范围：{task.plan.requested_modules.map(freshModuleLabel).join("、")}</p>}
                  <p className="mt-1">最多请求 {task.plan.max_external_requests} 次；实际费用尚无估价。</p>
                  <p className="mt-1 text-ink-muted">确认前不会开始采集。计划有效至 {task.expiresAt ? formatTime(task.expiresAt) : "待核对"}。</p>
                  <Button
                    className="mt-3 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    isDisabled={!canConfirm || stale || submitted.includes(task.id)}
                    onPress={() => {
                      setSubmitted((current) => [...current, task.id]);
                      setConfirmError("");
                      void onConfirm(task.id).catch((error: unknown) => {
                        setSubmitted((current) => current.filter((id) => id !== task.id));
                        setConfirmError(error instanceof Error ? error.message : "确认消息未能发送，请重试。");
                      });
                    }}
                  >
                    {submitted.includes(task.id) ? "确认已发送" : "确认开始现采"}
                  </Button>
                  {!canConfirm ? <p className="mt-2 text-xs text-amber-700">AI 额度不足，充值后可继续确认原计划。</p> : null}
                </div>
              ) : null}
              {task.knowledgeCutoff ? (
                <p className="mt-1 text-xs text-ink-muted">
                  来源：CWM 现采 · 数据截止 {formatTime(task.knowledgeCutoff)}
                </p>
              ) : (
                <p className="mt-1 text-xs text-ink-muted">来源：CWM 现采</p>
              )}
              {task.retryableReadError ? (
                <p className="mt-1 text-xs text-amber-700">
                  {freshReadErrorText(task.errorCode)}；任务尚未终止。可重新读取当前状态，不能新建第二个现采任务。
                </p>
              ) : null}
              {task.cancelRequestUnknown && isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-amber-700">
                  本地已保存取消请求，但不能证明 CWM 已接收；请读取原任务状态，不要重复提交。
                  {task.cancelRequestedAt ? ` · 请求时间 ${formatTime(task.cancelRequestedAt)}` : ""}
                </p>
              ) : task.cancelPending && isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-amber-700">
                  CWM 已接收取消请求，但尚未证明采集停止；请读取当前任务终态，不要直接重复提交。
                  {task.cancelRequestedAt ? ` · 请求时间 ${formatTime(task.cancelRequestedAt)}` : ""}
                </p>
              ) : isFreshTaskActive(task) ? (
                <p className="mt-1 text-xs text-ink-muted">
                  如需停止，请在对话中明确提出取消并引用此任务 ID；页面不会仅取消本地记录。
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {confirmError ? <p className="mt-3 text-sm text-red-700" role="alert">{confirmError}</p> : null}
    </section>
  );
}

function freshModuleLabel(module: string) {
  const names: Record<string, string> = {
    IDENTITY: "目标身份", LISTING: "商品信息", SHOP_ASSORTMENT: "店铺商品",
    OFFER: "报价", MEDIA_CREATIVE: "内容素材", REVIEW_RECORDS: "评论",
    CREATOR_AFFILIATE: "达人", AD_PRESENCE: "广告", OFFICIAL_OPERATIONS: "官方规则",
    MARKET_DEMAND_SIGNAL: "市场需求信号", CATEGORY_TREND: "类目趋势",
    KEYWORD_SERP: "关键词结果", LIVE_COMMERCE: "直播", COVERAGE: "覆盖情况", COMPARE: "对比",
  };
  return names[module] ?? module;
}

function freshDecisionFieldLabel(field: string) {
  const names: Record<string, string> = {
    creator_identity: "达人身份", creator_country: "达人所在市场", creator_category: "达人类目",
    creator_audience: "受众规模", creator_engagement: "内容互动", creator_commerce_estimate: "带货表现（估算）",
    creator_contact: "公开商务联系信息（可能缺失）", creator_recent_video: "近期视频元数据（可能缺失）",
  };
  return names[field] ?? field;
}

function freshSourceStabilityLabel(state: string) {
  if (state === "STABLE") return "近期任务完成情况较稳定";
  if (state === "DEGRADED") return "近期任务完成情况有波动";
  return "近期样本不足，稳定性待核实";
}

function freshReadErrorText(code: string) {
  switch (code) {
    case "cwm_gateway_unavailable": return "CWM 服务暂时不可用";
    case "cwm_gateway_contract_mismatch": return "CWM 返回了不符合当前协议的数据";
    case "cwm_gateway_response_oversize": return "CWM 返回内容超过读取上限";
    default: return "本次未取得可信的现采状态";
  }
}

function formatTime(value: string) {
  const milliseconds = Date.parse(value);
  if (!Number.isFinite(milliseconds)) return "时间未知";
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(milliseconds);
}
